import { Formik } from "formik";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { DesktopDatePicker as MUIDesktopDatePicker } from "@mui/x-date-pickers";
import { Grid, Checkbox, Typography } from "@mui/material";
import { ToggleOffOutlined, ToggleOnOutlined } from "@mui/icons-material";

import pages from "../../../../constants/pages";
import {
  AutocompleteMultiSelect,
  Label,
  Autocomplete,
  TextField,
} from "../../../../components";
import { colors } from "../../../css components/Style";
import { useAxios, useComponent } from "../../../../hooks";
import { PrimaryButton } from "../../../css components/Button";

const Heading = styled(Typography)`
  font-size: 15px;
  font-weight: 500;
  line-height: 27px;
  align-self: center;
`;

const DesktopDatePicker = styled(MUIDesktopDatePicker)`
  .MuiTextField-root {
    padding: 8px;
    border-color: #909090 !important;
  }

  & label.Mui-focused {
    color: ${colors.publicisBlue};
  }

  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: ${colors.publicisBlue};
    }
  }

  .MuiInputLabel-root {
    color: #333;
  }

  .MuiOutlinedInput-notchedOutline {
    border-radius: 5px;
  }

  .MuiOutlinedInput-root {
    font-size: 16px;
    border-color: ${colors.publicisBlue};
    border-radius: 5px;
  }

  .Mui-disabled {
    color: rgba(0, 0, 0, 0.66);
    font-size: 16px;
    border-radius: 5px;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.66);
  }

  .MuiInputLabel-outlined {
    font-size: 16px;
  }

  .Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: #f5f5f5;
    border: 1px solid #c4cacb;
  }

  #mui-7-helper-text {
    color: red;
  }
  .MuiInputLabel-asterisk {
    color: ${(props) => (Boolean(props.required && !props.value) ? "red" : "")};
  }
`;

export default function AudienceForm({
  audienceData,
  component,
  formikProps,
  editState,
}) {
  const axios = useAxios();
  const navigate = useNavigate();
  const { loader } = useComponent();
  const [dp, setDp] = useState([]);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    axios({
      url: "/adtech/getAllDynamicFields",
      method: "GET",
      disableRedirect: true,
      params: {
        ...(audienceData?.contributors?.value?.length > 0 && {
          contributor_id: audienceData?.contributors?.value.join(","),
        }),
      },
    })
      .then((response) => {
        if (response.status) {
          setFields(response.data);
        }
      })
      .catch((err) => console.error(err));

    if (audienceData?.contributorsChecked) {
      axios({
        url: "/user/getAllDataPartner",
        method: "get",
        disableRedirect: true,
        disableError: true,
      }).then((response) => {
        let contributors = [];
        if (response.status) {
          for (let contributor of response.data.data) {
            if (contributor.connectionDetails.status === "ACTIVE") {
              contributors.push({
                id: contributor.connectionDetails.brand_id,
                name: contributor.brandDetails.brand_name,
              });
            }
          }
          setDp(contributors);
        }
      });
    }
  }, [
    axios,
    loader,
    audienceData?.contributors?.value,
    audienceData.contributorsChecked,
  ]);

  return (
    <Fragment>
      <Formik enableReinitialize initialValues={audienceData}>
        {(formikProps) => (
          <Fragment>
            {audienceData.contributors?.value?.length > 0 && (
              <Grid size={12}>
                <Typography variant="h6">
                  Selective contributors :{" "}
                  {dp
                    .filter((brand) =>
                      audienceData.contributors?.value?.includes(brand?.id)
                    )
                    .map((d) => d.name)
                    .join(",")}
                </Typography>
              </Grid>
            )}

            <Grid size={4}>
              <TextField
                label="Audience Group Name"
                formikProps={formikProps}
                name="audience_group.value"
                placeholder="Audience Group Name"
                disabled
              />
            </Grid>

            {fields.map(
              (field, index) =>
                audienceData.hasOwnProperty([field.name]) && (
                  <Fragment key={index}>
                    {field.control_id === 3 && (
                      <Grid size={4}>
                        <TextField
                          value={audienceData[field.name]?.value}
                          label={field.label}
                          name={`${field.name}.value`}
                          placeholder={field.label}
                          disabled
                        />
                      </Grid>
                    )}

                    {field.control_id === 1 && (
                      <Fragment>
                        <Grid size={4}>
                          <div style={{ display: "flex", gap: "10px" }}>
                            <TextField
                              size="small"
                              label={field.options[0].label}
                              value={
                                audienceData[field.name]?.value[
                                  field.options[0]["value"]
                                ]
                              }
                              name={`${field.name}.value.${field.options[0]["value"]}`}
                              placeholder={field.options[0].label}
                              disabled
                            />

                            <TextField
                              size="small"
                              label={field.options[1].label}
                              value={
                                audienceData[field.name]?.value[
                                  field.options[1]["value"]
                                ]
                              }
                              name={`${field.name}.value.${field.options[1]["value"]}`}
                              placeholder={field.options[1].label}
                              disabled
                            />
                          </div>
                        </Grid>
                      </Fragment>
                    )}

                    {field.control_id === 2 && field.multi_select && (
                      <Grid size={4}>
                        <AutocompleteMultiSelect
                          label={field.label}
                          name={`${field.name}.value`}
                          value={audienceData[field.name]?.value}
                          disabled
                          defaults={{
                            primaryKey: "value",
                            displayLabel: "label",
                          }}
                          options={field.options}
                          formikProps={formikProps}
                          placeholder={field.placeholder}
                        />
                      </Grid>
                    )}

                    {field.control_id === 2 && !field.multi_select && (
                      <Grid size={4}>
                        <Autocomplete
                          label={field.label}
                          name={`${field.name}.value`}
                          placeholder={field.placeholder}
                          defaults={{
                            primaryKey: "value",
                            displayLabel: "label",
                          }}
                          sx={{
                            "& .MuiSvgIcon-root": {
                              display: "none",
                            },
                          }}
                          formikProps={formikProps}
                          options={field.options}
                          value={audienceData[field.name]?.value}
                        />
                      </Grid>
                    )}

                    {field.control_id === 4 && (
                      <Grid size={4}>
                        <DesktopDatePicker
                          sx={{ width: "100%" }}
                          label={field.label}
                          name={`${field.name}.value`}
                          formikProps={formikProps}
                          disabled
                          value={formikProps.values[field.name]?.value}
                          slotProps={{
                            textField: {
                              size: "small",
                            },
                          }}
                          onChange={(newValue) => {
                            let date = new Date(newValue);

                            let d =
                              date.getFullYear() +
                              "-" +
                              "0" +
                              (date.getMonth() + 1) +
                              "-" +
                              date.getDate();

                            formikProps.setValues((prevVal) => ({
                              ...prevVal,
                              [field.name]: {
                                ...prevVal[field.name],
                                value: d,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              disabled
                              name={`${field.name}.value`}
                              formikProps={formikProps}
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Fragment>
                )
            )}

            {audienceData?.additionalFields?.map((field, index) => (
              <Fragment>
                <Grid style={{ color: colors.publicisBlue }} size={10}>
                  <Typography>
                    {"Custom Audience Field " + (index + 1)}
                  </Typography>
                </Grid>

                <Grid size={3}>
                  <TextField
                    size="small"
                    label="Key"
                    disabled
                    value={field.key}
                    name={"additionalFields[" + index + "].key"}
                    placeholder="key"
                  />
                </Grid>

                <Grid size={3}>
                  <TextField
                    size="small"
                    disabled
                    label="Value"
                    value={field.value}
                    name={"additionalFields[" + index + "].value"}
                    placeholder="Value"
                  />
                </Grid>
              </Fragment>
            ))}
          </Fragment>
        )}
      </Formik>

      {/* {component === "" && !!formikProps.values.campaignID && editState && (
        <Grid size={12}>
          <PrimaryButton
            onClick={() =>
              navigate(
                pages.audienceDetails.route + audienceData.ID + "?mode=edit"
              )
            }
            style={{
              color: colors.publicisBlue,
              cursor: "pointer",
              padding: "0px",
            }}
          >
            Edit Detail
          </PrimaryButton>
        </Grid>
      )} */}
    </Fragment>
  );

  function Mandatory({ field }) {
    return (
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Heading
          style={{
            color: audienceData[field.name]?.mandatory ? "red" : "gray",
          }}
        >
          Mandatory
        </Heading>

        <Checkbox
          disabled
          name={`${field.name}.mandatory`}
          checked={audienceData[field.name]?.mandatory}
          icon={<ToggleOffOutlined fontSize="large" />}
          checkedIcon={
            <ToggleOnOutlined
              fontSize="large"
              style={{
                color: audienceData[field.name]?.mandatory ? "red" : "gray",
              }}
            />
          }
        />
      </div>
    );
  }
}
